import React from 'react';

import { useMedia } from '@tager/web-core';

import { Tooltip } from './components/Tooltip';
import { Body } from './components/Body';
import { GrabHandle } from './components/GrabHandle';
import { GrabTarget } from './components/GrabTarget';
import { Spread } from './components/Spread';
import { Rest } from './components/Rest';
import { MobileRest } from './components/MobileRest';
import { MobileSpread } from './components/MobileSpread';
import { useDraggable } from './hooks/useDraggable';
import { getBody } from './bodies';
import { CarViewProps } from './types';
import { Component, Container } from './styles';

export const CarView = React.memo<CarViewProps>(
  ({
    bodyType = 'crossover',
    value = 0,
    min = 0,
    max = 100,
    onChange,
    info = '',
  }) => {
    const body = React.useMemo(() => getBody(bodyType), [bodyType]);

    const spreadValue = React.useMemo<number>(
      () => (value < min ? min : value > 100 ? 100 : value),
      [min, value]
    );

    const restValue = React.useMemo(() => 100 - spreadValue, [spreadValue]);

    const { sliderRef, grabHandleRef, grabTargetRef } = useDraggable({
      min,
      value: spreadValue,
      max,
      onChange,
    });

    const mobileSpreadBadgeRef = React.useRef<HTMLSpanElement | null>(null);
    const mobileRestBadgeRef = React.useRef<HTMLSpanElement | null>(null);

    const isTablet = useMedia(`(max-width: 1259px)`);

    React.useEffect(() => {
      const mobileSpreadBadge = mobileSpreadBadgeRef.current;
      const mobileRestBadge = mobileRestBadgeRef.current;

      if (!mobileSpreadBadge || !mobileRestBadge) {
        return;
      }

      const mobileSpreadBadgeRect = mobileSpreadBadge.getBoundingClientRect();
      const mobileRestBadgeRect = mobileRestBadge.getBoundingClientRect();

      if (mobileSpreadBadgeRect.right >= mobileRestBadgeRect.left) {
        mobileRestBadge.style.transform = 'translateY(110%)';
      } else {
        mobileRestBadge.style.transform = '';
      }
    }, [value]);

    return (
      <Component ref={sliderRef}>
        <Container>
          <Body {...body} />

          {isTablet ? (
            <>
              <MobileSpread ref={mobileSpreadBadgeRef} value={spreadValue} />
              <MobileRest ref={mobileRestBadgeRef} value={restValue} />
              {info && <Tooltip info={info} />}
            </>
          ) : (
            <GrabTarget ref={grabTargetRef}>
              <Spread value={spreadValue} />
              <GrabHandle ref={grabHandleRef} />
              <Rest value={restValue} />
              {info && <Tooltip info={info} />}
            </GrabTarget>
          )}
        </Container>
      </Component>
    );
  }
);
